import deepEqual from 'deep-equal';

/**
 * Iterates over @param arr, performing deep equality checks on each index, to see if it contains @param obj.
 * @see {@link https://www.npmjs.com/package/deep-equal|deep-equal} for deep equality implementation.
 *
 * `Array.includes()` cannot be used on objects; @see {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/includes|Array.prototype.includes()},
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Equality_comparisons_and_sameness#same-value-zero_equality|same-value-zero-equality}
 * @summary Checks array for element existence.
 * @function
 * @param {Object[]} arr array of objects to be checked.
 * @param {Object} obj object of interest.
 * @returns {Number} index of object if found; otherwise, -1.
 */
export const arrayContainsObject = (arr, obj) => {
	for (let index in arr) {
		if (deepEqual(arr[index], obj)) {
			return index;
		}
	}
	return -1;
};

/**
 * @function
 * @name changeUniqueArray
 *
 * Maintains array of unique elements. Pushes to (or) splices from array
 * based on existence.
 * @param {Array} arr array of non-repeating elements.
 * @param {any} el recipient element of change.
 *
 * @returns {Array} updated array.
 * @example
 * // returns [{foo: true}, {foo: false}]
 * changeUniqueArray([{foo: true}], {foo: false})
 * // returns [{foo: true}]
 * changeUniqueArray([{foo: true}, {foo: false}], {foo: false})
 */
export const changeUniqueArray = (arr, el) => {
	let arrCopy = [...arr];
	const index =
		typeof el === 'object'
			? arrayContainsObject(arrCopy, el)
			: arrCopy.indexOf(el);
	if (index > -1) {
		arrCopy.splice(index, 1);
	} else {
		arrCopy.push(el);
	}
	return arrCopy;
};

/**
 * Allows you to remove a subset of properties from an object.
 *
 * @param {Object} obj - object you wish to remove props from
 * @param {string[]} propsToRemove - array of property keys
 * @returns - updated object without specified properties
 */
export const removeSpecifiedObjectProps = (obj, propsToRemove) => {
	const objCopy = { ...obj };

	propsToRemove.forEach(prop => {
		if (prop in objCopy) {
			delete objCopy[prop];
		}
	});

	return objCopy;
};

/**
 * Compare function useful for sorting array of objects.
 *
 * @param {object} firstEl first element for comparison.
 * @param {object} secondEl second element for comparison.
 * @param {string} key element property of interest to be used for comparison.
 * @param {number} order sort order; 1 for ascending, -1 for descending.
 * @returns {number} relative sort order between elements.
 */
export const compareFn = (firstEl, secondEl, key = '', order = 1) => {
	const ascending = order > 0;
	let valueFirstEl = firstEl && firstEl[key] ? firstEl[key] : '';
	let valueSecondEl = secondEl && secondEl[key] ? secondEl[key] : '';
	valueFirstEl =
		typeof valueFirstEl === 'string'
			? valueFirstEl.toLowerCase()
			: valueFirstEl;
	valueSecondEl =
		typeof valueSecondEl === 'string'
			? valueSecondEl.toLowerCase()
			: valueSecondEl;

	if (valueFirstEl < valueSecondEl) {
		return ascending ? -1 : 1;
	}
	if (valueFirstEl > valueSecondEl) {
		return ascending ? 1 : -1;
	}
	return 0;
};
